import { Deferred, Head, router, usePage } from "@inertiajs/react"
import { Eye, Shield01 } from "@untitledui/icons"
import { format } from "date-fns"

import { PageHeader } from "@/components/console/page-header"
import { SchoolLink } from "@/components/console/school-link"
import { StatusBadge, type StatusTier } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Notice } from "@/components/ui/notice"
import { Field, Fields, Panel } from "@/components/ui/panel"
import { Table, TableCard } from "@/components/ui/table"
import { TabList, Tabs } from "@/components/ui/tabs"
import { AdminLayout } from "@/layouts/admin-layout"
import {
  adminUserPath,
  adminUsersPath,
  impersonateAdminUserPath,
  toggleSuperAdminAdminUserPath,
} from "@/routes"

import type {
  EndedBy,
  Identity,
  ImpersonationRow,
  ImpersonationRows,
  Interrupted,
  RecentCall,
  SessionRow,
  SignIn,
  Stats,
  TurfRow,
  UserDetail,
} from "./types"

interface Props {
  user: UserDetail
  identity: Identity | null
  turfs: TurfRow[]
  stats: Stats
  /** Set when an expired impersonation of this user sent the admin here. */
  interrupted?: Interrupted | null
  sessions?: SessionRow[]
  recent_calls?: RecentCall[]
  impersonations?: ImpersonationRows
}

const SIGN_IN: Record<SignIn, string> = {
  salesforce: "Salesforce",
  password: "Password",
  awaiting_sign_in: "Awaiting first sign-in",
}

const REVIEW_TIER: Record<string, StatusTier> = {
  pending: "remote",
  ready: "caution",
  flagged: "caution",
  reviewed: "confirmed",
  skipped: "system",
}

const day = (iso: string) => format(new Date(iso), "d MMM yyyy")
const moment = (iso: string) => format(new Date(iso), "d MMM yyyy · HH:mm")

/**
 * One account, for the developers: who they are, how they sign in, the turfs
 * they own, what they have done, and the two acts: browse as them, and the
 * super-admin grant. The server refuses what the buttons hide.
 */
export default function AdminUserShow({
  user,
  identity,
  turfs,
  stats,
  interrupted,
  sessions,
  recent_calls,
  impersonations,
}: Props) {
  const { auth } = usePage().props
  const isSelf = auth.user.id === user.id
  const canImpersonate = !isSelf && !user.super_admin

  return (
    <AdminLayout>
      <Head title={user.name} />
      <div className="mx-auto flex max-w-7xl flex-col gap-6 px-4 lg:px-8">
        <PageHeader
          crumbs={[
            { label: "access" },
            { label: "users", href: adminUsersPath() },
            { label: user.name },
          ]}
          title={user.name}
          facts={`${user.email} · ${SIGN_IN[user.sign_in]}${user.super_admin ? " · super admin" : user.staff ? " · staff" : ""}`}
          actions={
            !isSelf && (
              <>
                {canImpersonate && (
                  <Button
                    size="md"
                    color="secondary"
                    iconLeading={Eye}
                    onClick={() =>
                      router.post(impersonateAdminUserPath(user.id))
                    }
                  >
                    Browse as {user.name.split(" ")[0]}
                  </Button>
                )}
                <Button
                  size="md"
                  color="tertiary"
                  iconLeading={Shield01}
                  onClick={() =>
                    router.patch(toggleSuperAdminAdminUserPath(user.id))
                  }
                >
                  {user.super_admin ? "Revoke super admin" : "Make super admin"}
                </Button>
              </>
            )
          }
        />

        {interrupted && (
          <InterruptedBand
            interrupted={interrupted}
            userId={user.id}
            canImpersonate={canImpersonate}
          />
        )}

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <Panel title="Account">
            <Fields>
              <Field label="Email">{user.email}</Field>
              <Field label="Mobile">{user.mobile_phone ?? "—"}</Field>
              <Field label="Signs in with">{SIGN_IN[user.sign_in]}</Field>
              <Field label="Email verified">
                {user.verified ? "Yes" : "No"}
              </Field>
              <Field label="Joined">{day(user.created_at)}</Field>
              <Field label="Last changed">{moment(user.updated_at)}</Field>
            </Fields>
          </Panel>

          <Panel
            title="Salesforce identity"
            description="What the write-back reads: a Task this person logs is owned by this Salesforce user."
          >
            {identity ? (
              <Fields>
                <Field label="Provider">{identity.provider}</Field>
                <Field label="Name in Salesforce">{identity.name ?? "—"}</Field>
                <Field label="Salesforce user id">
                  <span className="font-mono text-xs">
                    {identity.salesforce_user_id ?? "—"}
                  </span>
                </Field>
                <Field label="Org id">
                  <span className="font-mono text-xs">
                    {identity.salesforce_org_id ?? "—"}
                  </span>
                </Field>
                <Field label="Token expires">
                  {identity.token_expires_at
                    ? moment(identity.token_expires_at)
                    : "—"}
                </Field>
                <Field label="Last sign-in">
                  {moment(identity.updated_at)}
                </Field>
              </Fields>
            ) : (
              <Notice tone="caution" role="status">
                No Salesforce identity yet. It attaches on this person's first
                Salesforce sign-in, by email; until then they cannot use the
                console and no Task can be owned by them.
              </Notice>
            )}
          </Panel>

          <Panel
            title="Turfs"
            description="The areas of ownership this person holds. A job in one of these regions resolves to them."
            note={`${turfs.length} ${turfs.length === 1 ? "turf" : "turfs"}`}
          >
            {turfs.length === 0 ? (
              <p className="text-tertiary text-sm">Owns no turf.</p>
            ) : (
              <ul className="flex flex-col gap-2">
                {turfs.map((turf) => (
                  <li
                    key={turf.id}
                    className="flex items-center justify-between gap-3"
                  >
                    <span className="text-primary text-sm">
                      {turf.label}{" "}
                      <span className="text-quaternary font-mono text-xs">
                        {turf.key}
                      </span>
                    </span>
                    <StatusBadge tier={turf.active ? "confirmed" : "system"}>
                      {turf.active ? "active" : "inactive"}
                    </StatusBadge>
                  </li>
                ))}
              </ul>
            )}
          </Panel>

          <Panel title="Activity">
            <Fields>
              <Field label="Calls">{stats.calls}</Field>
              <Field label="Of which voice">{stats.voice_calls}</Field>
              <Field label="Call lists built">{stats.lists_built}</Field>
              <Field label="Tunings">{stats.tunings}</Field>
              <Field label="Outreach attempts">{stats.outreach_attempts}</Field>
              <Field label="Sessions">{stats.sessions}</Field>
            </Fields>
          </Panel>
        </div>

        <Tabs defaultSelectedKey="sessions">
          <TabList
            size="sm"
            type="underline"
            items={[
              {
                id: "sessions",
                label: "Sessions",
                badge: stats.sessions || undefined,
              },
              {
                id: "calls",
                label: "Recent calls",
                badge: stats.calls || undefined,
              },
              {
                id: "browsed",
                label: "Browsed as",
                badge: stats.browsed_as || undefined,
              },
            ]}
          />
          <Tabs.Panel id="sessions" className="pt-4">
            <Deferred data="sessions" fallback={<TabSkeleton />}>
              <SessionsPanel rows={sessions ?? []} />
            </Deferred>
          </Tabs.Panel>
          <Tabs.Panel id="calls" className="pt-4">
            <Deferred data="recent_calls" fallback={<TabSkeleton />}>
              <CallsPanel rows={recent_calls ?? []} />
            </Deferred>
          </Tabs.Panel>
          <Tabs.Panel id="browsed" className="pt-4">
            <Deferred data="impersonations" fallback={<TabSkeleton />}>
              <ImpersonationsPanel
                rows={impersonations ?? { of_me: [], by_me: [] }}
                user={user}
              />
            </Deferred>
          </Tabs.Panel>
        </Tabs>
      </div>
    </AdminLayout>
  )
}

function TabSkeleton() {
  return <div className="card h-24 animate-pulse" />
}

function SessionsPanel({ rows }: { rows: SessionRow[] }) {
  return (
    <TableCard.Root>
      <TableCard.Caption
        title="Sessions"
        count={`${rows.length} ${rows.length === 1 ? "session" : "sessions"}`}
        note="newest ↑"
      />
      {rows.length === 0 ? (
        <TableCard.Empty>
          No sessions: this person has never signed in.
        </TableCard.Empty>
      ) : (
        <Table aria-label="Sessions">
          <Table.Header>
            <Table.Head
              id="device"
              isRowHeader
              label="Device"
              className="w-full"
            />
            <Table.Head id="source" label="Source" />
            <Table.Head id="ip" label="IP" />
            <Table.Head id="started" label="Started" align="right" />
          </Table.Header>
          <Table.Body items={rows}>
            {(row) => (
              <Table.Row id={row.id}>
                <Table.Cell className="max-w-md truncate whitespace-normal">
                  {row.user_agent ?? "Unknown device"}
                </Table.Cell>
                <Table.Status
                  tier={
                    row.source === "impersonation"
                      ? "caution"
                      : row.current
                        ? "remote"
                        : "neutral"
                  }
                >
                  {row.source === "impersonation"
                    ? "impersonation"
                    : row.current
                      ? "this session"
                      : "sign-in"}
                </Table.Status>
                <Table.Id>{row.ip_address}</Table.Id>
                <Table.When at={row.created_at} relative />
              </Table.Row>
            )}
          </Table.Body>
        </Table>
      )}
    </TableCard.Root>
  )
}

function CallsPanel({ rows }: { rows: RecentCall[] }) {
  return (
    <TableCard.Root>
      <TableCard.Caption
        title="Recent calls"
        count={`last ${rows.length}`}
        note="newest ↑"
      />
      {rows.length === 0 ? (
        <TableCard.Empty>No calls made or logged yet.</TableCard.Empty>
      ) : (
        <Table aria-label="Recent calls">
          <Table.Header>
            <Table.Head
              id="school"
              isRowHeader
              label="School"
              className="w-full"
            />
            <Table.Head id="kind" label="Kind" />
            <Table.Head id="outcome" label="Outcome" />
            <Table.Head id="review" label="Review" />
            <Table.Head id="when" label="When" align="right" />
          </Table.Header>
          <Table.Body items={rows}>
            {(row) => (
              <Table.Row id={row.id}>
                <Table.Cell className="text-primary font-medium">
                  {row.school ? (
                    <SchoolLink school={row.school} />
                  ) : (
                    row.school_name
                  )}
                </Table.Cell>
                <Table.Cell>
                  {row.voice ? "Voice" : "Logged by hand"}
                </Table.Cell>
                <Table.Cell>{row.disposition ?? "—"}</Table.Cell>
                <Table.Status tier={REVIEW_TIER[row.review_state] ?? "neutral"}>
                  {row.review_state}
                </Table.Status>
                <Table.When at={row.created_at} />
              </Table.Row>
            )}
          </Table.Body>
        </Table>
      )}
    </TableCard.Root>
  )
}

const ENDED_BY: Record<EndedBy, string> = {
  stopped: "stopped",
  expired: "hour ran out",
  admin_signed_out: "developer signed out",
  revoked: "session revoked",
  account_deleted: "account deleted",
  session_destroyed: "session ended",
}

/*
 * The record of browsing, both ways round. Every account has the first
 * ledger; the second appears for a super admin, or for anyone with history
 * in it, since a revoked grant does not erase what was done under it.
 */
function ImpersonationsPanel({
  rows,
  user,
}: {
  rows: ImpersonationRows
  user: UserDetail
}) {
  const first = user.name.split(" ")[0]
  return (
    <div className="flex flex-col gap-4">
      <ImpersonationsLedger
        title={`Browsed as ${first}`}
        who="impersonator"
        whoLabel="By"
        rows={rows.of_me}
        empty={`Nobody has browsed as ${first}.`}
      />
      {(user.super_admin || rows.by_me.length > 0) && (
        <ImpersonationsLedger
          title={`${first} has browsed as`}
          who="target"
          whoLabel="As"
          rows={rows.by_me}
          empty={`${first} has not browsed as anyone.`}
        />
      )}
    </div>
  )
}

/*
 * Active rows carry the caution tone: a state a person ends. A row with no
 * end and no session was ended by a bulk delete with no callback, so it says
 * "ended, time unknown" rather than inventing one.
 */
function ImpersonationsLedger({
  title,
  who,
  whoLabel,
  rows,
  empty,
}: {
  title: string
  who: "impersonator" | "target"
  whoLabel: string
  rows: ImpersonationRow[]
  empty: string
}) {
  return (
    <TableCard.Root>
      <TableCard.Caption
        title={title}
        count={`${rows.length} ${rows.length === 1 ? "time" : "times"}`}
        note="newest ↑"
      />
      {rows.length === 0 ? (
        <TableCard.Empty>{empty}</TableCard.Empty>
      ) : (
        <Table aria-label={title}>
          <Table.Header>
            <Table.Head
              id="who"
              isRowHeader
              label={whoLabel}
              className="w-full"
            />
            <Table.Head id="started" label="Started" align="right" />
            <Table.Head id="ended" label="Ended" align="right" />
            <Table.Head id="how" label="How it ended" />
          </Table.Header>
          <Table.Body items={rows}>
            {(row) => {
              const person = row[who]
              return (
                <Table.Row
                  id={row.id}
                  href={person ? adminUserPath(person.id) : undefined}
                  tone={row.active ? "caution" : undefined}
                >
                  <Table.Cell className="text-primary font-medium">
                    {person ? (
                      person.name
                    ) : (
                      <span className="text-quaternary font-normal">
                        a deleted account
                      </span>
                    )}
                  </Table.Cell>
                  <Table.When at={row.started_at} />
                  {row.ended_at || row.active ? (
                    <Table.When at={row.ended_at} />
                  ) : (
                    <Table.Id align="right">unknown</Table.Id>
                  )}
                  <Table.Status tier={row.active ? "caution" : "system"}>
                    {row.active
                      ? "browsing now"
                      : row.ended_by
                        ? ENDED_BY[row.ended_by]
                        : "ended, time unknown"}
                  </Table.Status>
                </Table.Row>
              )
            }}
          </Table.Body>
        </Table>
      )}
    </TableCard.Root>
  )
}

/*
 * The hour browsing as this person ran out mid-request. Says what was
 * interrupted and, for a page, offers to pick it up again as them; a form
 * post was discarded on purpose and has to be redone once back as them.
 * Caution grammar, as the browsing pill: a state a person ends.
 */
function InterruptedBand({
  interrupted,
  userId,
  canImpersonate,
}: {
  interrupted: Interrupted
  userId: number
  canImpersonate: boolean
}) {
  const first = interrupted.user_name.split(" ")[0]
  const wasPage = interrupted.method === "GET" || interrupted.method === "HEAD"
  const resume = () =>
    router.post(impersonateAdminUserPath(userId), {
      return_to: interrupted.path,
    })

  return (
    <div
      role="status"
      className="caution-band text-caution-700 flex flex-wrap items-center gap-x-4 gap-y-2 px-3.5 py-2.5 text-sm"
    >
      <p className="grow">
        <span className="font-medium">
          Your hour browsing as {interrupted.user_name} ran out.
        </span>{" "}
        You are yourself again.{" "}
        {wasPage ? (
          <>
            You were on <code className="font-mono">{interrupted.path}</code>.
          </>
        ) : (
          <>
            A {interrupted.method} to{" "}
            <code className="font-mono">{interrupted.path ?? "a page"}</code>{" "}
            was not sent, so nothing was changed in your name: browse as {first}{" "}
            again and resubmit it.
          </>
        )}
      </p>
      {canImpersonate && interrupted.path && (
        <Button size="sm" color="secondary" iconLeading={Eye} onClick={resume}>
          {wasPage ? `Pick up as ${first}` : `Browse as ${first} again`}
        </Button>
      )}
    </div>
  )
}
